import { useCallback, useEffect, useState } from "react";
import { computePostureScore } from "../../utils/postureScore.js";

const SECTION_META = {
  security: {
    title: "Security gaps",
    subtitle: "Genuine posture weaknesses supported by assessment findings",
    accent: "var(--critical)",
  },
  operational: {
    title: "Operational-burden gaps",
    subtitle: "Not insecure — self-managed effort Ory paid tiers can remove",
    accent: "var(--warning)",
  },
  unverified: {
    title: "Unverified — confirm these",
    subtitle: "Hooks to verify; not claimed weaknesses until confirmed",
    accent: "var(--text-dim)",
  },
};

function ConfidenceBadge({ confidence }) {
  const observed = confidence === "observed";
  return (
    <span
      className="rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
      style={{
        background: observed ? "rgba(34,197,94,0.12)" : "rgba(148,163,184,0.15)",
        color: observed ? "var(--healthy)" : "var(--text-dim)",
      }}
    >
      {observed ? "Observed" : "Inferred"}
    </span>
  );
}

function GapCard({ gap }) {
  return (
    <article
      className="rounded-lg border p-4"
      style={{ borderColor: "var(--border)", background: "var(--bg-inset)" }}
    >
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <h3 className="text-[13px] font-semibold" style={{ color: "var(--text-strong)" }}>
          {gap.title}
        </h3>
        <ConfidenceBadge confidence={gap.confidence} />
      </div>
      <p className="mb-3 text-[12px] leading-relaxed" style={{ color: "var(--text-muted)" }}>
        {gap.explanation}
      </p>
      <dl className="grid gap-1 text-[11px]" style={{ color: "var(--text-dim)" }}>
        <div>
          <dt className="inline font-semibold" style={{ color: "var(--text-muted)" }}>
            Ory remedy:{" "}
          </dt>
          <dd className="inline">{gap.ory_remedy}</dd>
        </div>
        <div>
          <dt className="inline font-semibold" style={{ color: "var(--text-muted)" }}>
            Tier:{" "}
          </dt>
          <dd className="inline">{gap.tier}</dd>
        </div>
        {Array.isArray(gap.framework_refs) && gap.framework_refs.length > 0 ? (
          <div>
            <dt className="inline font-semibold" style={{ color: "var(--text-muted)" }}>
              Frameworks:{" "}
            </dt>
            <dd className="inline">
              {gap.framework_refs.map((f) => `${f.framework} ${f.section || ""}`.trim()).join(" · ")}
            </dd>
          </div>
        ) : null}
        {Array.isArray(gap.supporting_rule_ids) && gap.supporting_rule_ids.length > 0 ? (
          <div>
            <dt className="inline font-semibold" style={{ color: "var(--text-muted)" }}>
              Supporting rules:{" "}
            </dt>
            <dd className="inline font-mono text-[10px]">{gap.supporting_rule_ids.join(", ")}</dd>
          </div>
        ) : null}
      </dl>
    </article>
  );
}

function GapSection({ sectionKey, items }) {
  const meta = SECTION_META[sectionKey];
  return (
    <section className="mb-8" aria-labelledby={`gap-section-${sectionKey}`}>
      <header className="mb-3">
        <h2
          id={`gap-section-${sectionKey}`}
          className="text-[14px] font-bold"
          style={{ color: "var(--text-strong)" }}
        >
          <span
            aria-hidden
            className="mr-2 inline-block h-2 w-2 rounded-full"
            style={{ background: meta.accent }}
          />
          {meta.title}
          <span className="ml-2 text-[12px] font-normal" style={{ color: "var(--text-dim)" }}>
            ({items.length})
          </span>
        </h2>
        <p className="mt-1 text-[11px]" style={{ color: "var(--text-dim)" }}>
          {meta.subtitle}
        </p>
      </header>
      {items.length === 0 ? (
        <p className="text-[12px] italic" style={{ color: "var(--text-faint)" }}>
          None for current findings.
        </p>
      ) : (
        <div className="flex flex-col gap-3">{items.map((g) => <GapCard key={g.gap_id} gap={g} />)}</div>
      )}
    </section>
  );
}

export default function GapAnalysisTabContent({
  projectId,
  snapshotId,
  auditData,
  orgName,
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [shareText, setShareText] = useState("");
  const [copyOk, setCopyOk] = useState(false);

  const localPosture = auditData ? computePostureScore(auditData) : null;

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const qs = snapshotId ? `?snapshotId=${encodeURIComponent(snapshotId)}` : "";
      const res = await fetch(`/api/projects/${encodeURIComponent(projectId)}/gap-analysis${qs}`, {
        credentials: "include",
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body?.error || `Gap analysis failed (${res.status})`);
      }
      setAnalysis(body.analysis);
      setShareText(body.shareText || "");
    } catch (e) {
      setError(e?.message || "Failed to load gap analysis");
      setAnalysis(null);
    } finally {
      setLoading(false);
    }
  }, [projectId, snapshotId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleCopy = async () => {
    if (!shareText) return;
    try {
      await navigator.clipboard.writeText(shareText);
      setCopyOk(true);
      setTimeout(() => setCopyOk(false), 2000);
    } catch {
      setCopyOk(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <p className="text-[13px]" style={{ color: "var(--text-dim)" }}>
        Loading gap analysis…
      </p>
    );
  }

  if (error) {
    return (
      <div
        className="rounded-lg border p-4 text-[13px]"
        style={{ borderColor: "var(--border)", color: "var(--text-muted)" }}
      >
        {error}
      </div>
    );
  }

  if (!analysis) return null;

  return (
    <div className="gap-analysis-print mx-auto max-w-3xl pb-6">
      <header className="mb-6">
        <p className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: "var(--accent)" }}>
          Ory upgrade path
        </p>
        <h1 className="mt-1 text-[20px] font-bold" style={{ color: "var(--text-strong)" }}>
          Gap Analysis
        </h1>
        {orgName ? (
          <p className="mt-1 text-[13px]" style={{ color: "var(--text-muted)" }}>
            {orgName}
          </p>
        ) : null}
        {localPosture?.score != null ? (
          <p className="mt-2 text-[12px]" style={{ color: "var(--text-dim)" }}>
            Posture score (unchanged by this view):{" "}
            <strong style={{ color: "var(--text-strong)" }}>{localPosture.score}</strong>
          </p>
        ) : null}
      </header>

      <div
        className="mb-6 rounded-lg border px-4 py-3 text-[12px] leading-relaxed"
        style={{
          borderColor: "var(--warning)",
          background: "rgba(234,179,8,0.08)",
          color: "var(--text-muted)",
        }}
        role="note"
      >
        {analysis.disclaimer}
      </div>

      <div className="mb-6 flex flex-wrap gap-2 print:hidden">
        <button
          type="button"
          onClick={() => void handleCopy()}
          className="rounded-md px-3 py-1.5 text-[12px] font-semibold"
          style={{
            background: "var(--bg-inset)",
            border: "1px solid var(--border)",
            color: "var(--text-strong)",
          }}
        >
          {copyOk ? "Copied" : "Copy share summary"}
        </button>
        <button
          type="button"
          onClick={handlePrint}
          className="rounded-md px-3 py-1.5 text-[12px] font-semibold"
          style={{
            background: "var(--accent)",
            border: "none",
            color: "#fff",
          }}
        >
          Print / save PDF
        </button>
      </div>

      <GapSection sectionKey="security" items={analysis.security || []} />
      <GapSection sectionKey="operational" items={analysis.operational || []} />
      <GapSection sectionKey="unverified" items={analysis.unverified || []} />

      <footer
        className="mt-8 border-t pt-4 text-[10px]"
        style={{ borderColor: "var(--border)", color: "var(--text-faint)" }}
      >
        Catalog v{analysis.catalog_version} · Generated {analysis.generated_at} · Secret-safe summary
        (posture only, no config values)
      </footer>
    </div>
  );
}
